import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Progress, Select, Spin, Tabs, Tooltip, notification } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import AdminView from "./AdminView";
import TeachAILockPage from "./TeachAILockPage";
import styles from "./style.module.scss";

// "Yapay zekâya öğret": reviews the sentiment AI was unsure about, answered in one click.
// The API only returns questions of dashboards the user may open (api/active_learning.py).

const ANSWERS = [
  { value: "1.0", key: "positive", hotkey: "1", tone: "pos" },
  { value: "0.0", key: "negative", hotkey: "2", tone: "neg" },
  { value: "0.5", key: "neutral", hotkey: "3", tone: "neu" },
  { value: "33", key: "mixed", hotkey: "4", tone: "mix" },
];
const TONE = Object.fromEntries(ANSWERS.map((a) => [a.value, a.tone]));
const PERIODS = { "7": 7, "30": 30, all: null };
const PAGE = 20;

const percent = (value) => (value == null ? null : Math.round(value * 100));

function sinceFor(period) {
  const days = PERIODS[period];
  if (!days) return undefined;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function daysAgo(iso, t) {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return t("teach_ai.today_label");
  return t("teach_ai.days_ago", { count: days });
}

// the sentence inside the whole answer, highlighted; falls back to the sentence alone
function HighlightedText({ text, sentence }) {
  const at = text && sentence ? text.indexOf(sentence) : -1;
  if (at < 0) return <mark>{sentence}</mark>;
  return (
    <>
      {text.slice(0, at)}
      <mark>{sentence}</mark>
      {text.slice(at + sentence.length)}
    </>
  );
}

const Representation = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [waiting, setWaiting] = useState(0);
  const [today, setToday] = useState({ answered: 0, limit: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState([]); // [{item, value}] most recent first, for undo
  const [source, setSource] = useState(undefined);
  const [language, setLanguage] = useState(undefined);
  const [period, setPeriod] = useState("all");
  // the technical view exists only for Pivony experts: the API answers 403 to everyone else
  const [adminOverview, setAdminOverview] = useState(null);
  const focused = items[0];
  const busyRef = useRef(false);

  const sourceName = useCallback((s) => t(`teach_ai.source.${s}`, s), [t]);
  const answerLabel = useCallback(
    (value) => t(`teach_ai.answer.${ANSWERS.find((a) => a.value === value)?.key || "neutral"}`),
    [t]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE) });
    if (source) params.set("source", source);
    if (language) params.set("language", language);
    const since = sinceFor(period);
    if (since) params.set("since", since);
    const [s, q] = await Promise.all([
      apiRequest("active-learning/summary", "GET", null, true),
      apiRequest(`active-learning/queue?${params.toString()}`, "GET", null, true),
    ]);
    setSummary(s && !s.error ? s : { enabled: false });
    setItems(q?.items || []);
    setWaiting(q?.waiting || 0);
    setToday(q?.today || { answered: 0, limit: 0 });
    setLoading(false);
  }, [source, language, period]);

  useEffect(() => {
    load();
  }, [load]);

  const loadAdmin = useCallback(async () => {
    const res = await apiRequest("active-learning/admin/overview", "GET", null, true);
    setAdminOverview(res && !res.error && res.config ? res : null);
  }, []);

  useEffect(() => {
    loadAdmin();
  }, [loadAdmin]);

  const send = useCallback(
    async (item, value) => {
      if (!item || busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      const res = await apiRequest(`active-learning/queue/${item.id}/answer`, "POST", { answer: value }, true);
      busyRef.current = false;
      setBusy(false);
      if (!res || res.error) {
        notification.error({ message: t("teach_ai.save_failed") });
        return;
      }
      setItems((prev) => {
        const rest = prev.filter((i) => i.id !== item.id);
        return value === "skip" ? [...rest, item] : rest;
      });
      if (value !== "skip") {
        setAnswered((prev) => [{ item, value }, ...prev].slice(0, 5));
        setWaiting((w) => Math.max(0, w - 1));
        setToday((d) => ({ ...d, answered: d.answered + 1 }));
        setSummary((s) => (s?.pool ? { ...s, pool: { ...s.pool, count: s.pool.count + 1 } } : s));
      }
    },
    [t]
  );

  const undo = useCallback(
    async (entry) => {
      const res = await apiRequest(`active-learning/queue/${entry.item.id}/undo`, "POST", {}, true);
      if (!res || res.error) {
        notification.error({ message: t("teach_ai.undo_failed") });
        return;
      }
      setAnswered((prev) => prev.filter((e) => e !== entry));
      setItems((prev) => [entry.item, ...prev]);
      setWaiting((w) => w + 1);
      setToday((d) => ({ ...d, answered: Math.max(0, d.answered - 1) }));
      setSummary((s) => (s?.pool ? { ...s, pool: { ...s.pool, count: Math.max(0, s.pool.count - 1) } } : s));
    },
    [t]
  );

  // 1-4 answer the first card, S skips it (not while typing in a field)
  useEffect(() => {
    const onKey = (e) => {
      if (!focused || e.metaKey || e.ctrlKey || e.altKey) return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName) || e.target?.isContentEditable) return;
      const hit = ANSWERS.find((a) => a.hotkey === e.key);
      if (hit) send(focused, hit.value);
      else if (e.key === "s" || e.key === "S") send(focused, "skip");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focused, send]);

  const quality = summary?.quality;
  const delta = useMemo(() => {
    if (quality?.accuracy == null || quality?.previous_accuracy == null) return null;
    return percent(quality.accuracy) - percent(quality.previous_accuracy);
  }, [quality]);
  const remainingToday = Math.max(0, (today.limit || 0) - (today.answered || 0));

  const buttons = (item, extraClass = "") => (
    <div className={`${styles.buttons} ${extraClass}`}>
      {ANSWERS.map((a) => (
        <Button key={a.value} className={`${styles.answer} ${styles[a.tone]}`} disabled={busy} onClick={() => send(item, a.value)}>
          <span className={styles.dot} />
          {t(`teach_ai.answer.${a.key}`)}
          <kbd className={styles.kbd}>{a.hotkey}</kbd>
        </Button>
      ))}
      <Button className={`${styles.answer} ${styles.skip}`} type="text" disabled={busy} onClick={() => send(item, "skip")}>
        {t("teach_ai.answer.skip")}
        <kbd className={styles.kbd}>S</kbd>
      </Button>
    </div>
  );

  if (loading && !summary) {
    return (
      <div className={styles.center}>
        <Spin />
      </div>
    );
  }

  const adminTab = adminOverview ? <AdminView overview={adminOverview} onChanged={loadAdmin} /> : null;

  if (!summary?.enabled) {
    return (
      <>
        <TeachAILockPage />
        {adminTab && <div className={styles.page}>{adminTab}</div>}
      </>
    );
  }

  const labelling = (
    <>
      <div className={styles.kpis}>
        <div className={styles.card}>
          <h3>{t("teach_ai.next_improvement")}</h3>
          <div className={styles.big}>
            {summary.pool.count.toLocaleString()} <small>/ {t("teach_ai.reviews", { count: summary.pool.target })}</small>
          </div>
          <Progress
            className={styles.bar}
            percent={Math.min(100, Math.round((summary.pool.count / Math.max(1, summary.pool.target)) * 100))}
            showInfo={false}
            strokeColor={{ from: "#7C3AED", to: "#EC4899" }}
          />
          <p className={styles.note}>{t("teach_ai.next_improvement_note", { count: summary.pool.target })}</p>
        </div>

        <div className={styles.card}>
          <h3>{t("teach_ai.accuracy")}</h3>
          {quality?.accuracy != null ? (
            <>
              <div className={styles.big}>%{percent(quality.accuracy)}</div>
              {delta != null && (
                <p className={styles.note}>
                  <span className={delta >= 0 ? styles.up : styles.down}>
                    {delta >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {t("teach_ai.points", { count: Math.abs(delta) })}
                  </span>{" "}
                  {t("teach_ai.since_previous", { value: percent(quality.previous_accuracy) })}
                </p>
              )}
              <div className={styles.chips}>
                {Object.entries(quality.by_source || {}).map(([s, acc]) =>
                  acc == null ? null : (
                    <span key={s} className={styles.chip}>
                      {sourceName(s)} <b>%{percent(acc)}</b>
                    </span>
                  )
                )}
              </div>
            </>
          ) : (
            <p className={styles.note}>{t("teach_ai.accuracy_pending")}</p>
          )}
        </div>

        <div className={`${styles.card} ${styles.todayCard}`}>
          <h3>{t("teach_ai.today")}</h3>
          <div className={styles.big}>
            {today.answered} <small>/ {today.limit}</small>
          </div>
          <Progress
            className={styles.bar}
            percent={Math.min(100, Math.round((today.answered / Math.max(1, today.limit)) * 100))}
            showInfo={false}
            strokeColor={{ from: "#7C3AED", to: "#EC4899" }}
          />
          <p className={styles.note}>{t("teach_ai.waiting", { count: waiting })}</p>
        </div>
      </div>

      <div className={styles.filters}>
        <Select
          allowClear
          className={styles.select}
          placeholder={t("teach_ai.filter_source")}
          value={source}
          onChange={setSource}
          options={(summary.sources || []).map((s) => ({ value: s, label: sourceName(s) }))}
        />
        <Select
          allowClear
          className={styles.select}
          placeholder={t("teach_ai.filter_language")}
          value={language}
          onChange={setLanguage}
          options={[
            { value: "tr", label: t("teach_ai.language.tr") },
            { value: "diger", label: t("teach_ai.language.other") },
          ]}
        />
        <Select
          className={styles.select}
          value={period}
          onChange={setPeriod}
          options={[
            { value: "7", label: t("teach_ai.period.7") },
            { value: "30", label: t("teach_ai.period.30") },
            { value: "all", label: t("teach_ai.period.all") },
          ]}
        />
        <span className={styles.count}>{t("teach_ai.waiting", { count: waiting })}</span>
      </div>

      {answered.map((entry) => (
        <div key={`done-${entry.item.id}`} className={`${styles.review} ${styles.answered}`}>
          <span className={styles.source}>{sourceName(entry.item.source)}</span>
          <span className={styles.answeredText}>“{entry.item.sentence}”</span>
          <span className={styles.topic}>{entry.item.topic}</span>
          <span className={styles.answeredText}>→ {t("teach_ai.your_answer")}</span>
          <span className={`${styles.pill} ${styles[TONE[entry.value]]}`}>{answerLabel(entry.value)}</span>
          <Button type="link" size="small" className={styles.undo} onClick={() => undo(entry)}>
            {t("teach_ai.undo")}
          </Button>
        </div>
      ))}

      {loading ? (
        <div className={styles.center}>
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          {remainingToday === 0 && waiting > 0 ? t("teach_ai.done_for_today") : t("teach_ai.nothing_waiting")}
        </div>
      ) : (
        items.map((item, index) => (
          <div key={item.id} className={`${styles.review} ${index === 0 ? styles.focus : ""}`}>
            <div className={styles.meta}>
              <span className={styles.source}>{sourceName(item.source)}</span>
              <span>{daysAgo(item.created_at, t)}</span>
              {item.language && (
                <>
                  <span>·</span>
                  <span>{item.language === "tr" ? t("teach_ai.language.tr") : t("teach_ai.language.other")}</span>
                </>
              )}
              {/* golden-set questions are checks picked at random to measure the AI, not cases it was unsure about */}
              {item.golden ? (
                <Tooltip title={t("teach_ai.control_hint")}>
                  <span className={styles.control}>{t("teach_ai.control_question")}</span>
                </Tooltip>
              ) : (
                <span className={styles.unsure}>{t("teach_ai.ai_unsure")}</span>
              )}
            </div>
            <p className={styles.text}>
              {item.question ? (
                <>
                  {t("teach_ai.question")}: <i>{item.question}</i>
                  <br />
                </>
              ) : null}
              <HighlightedText text={item.text} sentence={item.sentence} />
            </p>
            <div className={styles.ask}>
              {t("teach_ai.ask_before")} <span className={styles.topic}>{item.topic}</span> {t("teach_ai.ask_after")}
              {item.ai_guess && (
                <span className={styles.guess}>
                  {t("teach_ai.ai_guess")}: <span className={`${styles.pill} ${styles[TONE[item.ai_guess]]}`}>{answerLabel(item.ai_guess)}</span>
                </span>
              )}
            </div>
            {buttons(item)}
          </div>
        ))
      )}

      {focused && <div className={styles.sticky}>{buttons(focused, styles.stickyButtons)}</div>}
    </>
  );

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t("teach_ai.title")}</h1>
      <p className={styles.lead}>{t("teach_ai.lead")}</p>
      {adminTab ? (
        <Tabs
          items={[
            { key: "label", label: t("teach_ai.tab_label"), children: labelling },
            { key: "admin", label: t("teach_ai.tab_admin"), children: adminTab },
          ]}
        />
      ) : (
        labelling
      )}
    </div>
  );
};

export default Representation;
